import { useRef } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { EASE, useReduceMotion } from "@/components/Reveal";

// Tarjeta desplegable de la portada: cerrada muestra una foto (o un ícono
// grande sobre fondo azul cuando no hay foto) con el título encima. Al tocar
// se abre con una animación suave de 0,4 s: la foto se agranda un poco, el
// texto aparece con fundido y el «+» gira hasta convertirse en «×».
// El contenido está en la página desde el principio (solo se despliega).
// Con «reducir movimiento» se abre y se cierra sin animación.
// Prop compacto: área de ícono más baja (16/9) para desplegables únicos a lo
// ancho del bloque; por defecto (grillas de 2+ columnas) es cuadrada en celular.
export default function Desplegable({ id, titulo, foto, alt, icono: Icono, compacto = false, abierto, onToggle, testid, children }) {
  const ref = useRef(null);
  const reduce = useReduceMotion();

  const toggle = () => {
    onToggle(id);
    if (!abierto && window.innerWidth < 768) {
      // En celular, la pantalla baja sola hasta mostrar la tarjeta completa.
      setTimeout(() => ref.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "nearest" }), 430);
    }
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={`overflow-hidden border transition-colors duration-300 ${
        abierto ? "border-certero/70 bg-surface" : "border-hud bg-ink hover:border-certero/40"
      }`}
    >
      <button data-testid={`${testid}-btn`} onClick={toggle} aria-expanded={abierto} className="group relative block w-full text-left">
        {foto ? (
          <span className="relative block overflow-hidden">
            <img
              src={foto}
              alt={alt || titulo}
              loading="lazy"
              decoding="async"
              className={`aspect-[4/3] w-full object-cover transition-transform duration-500 ${
                abierto ? "scale-[1.06]" : "group-hover:scale-[1.04]"
              }`}
            />
            <span className="absolute inset-0 bg-gradient-to-t from-abyss/85 via-abyss/25 to-transparent" aria-hidden="true" />
          </span>
        ) : (
          <span
            className={`relative flex w-full items-center justify-center overflow-hidden bg-[#0c295c]/60 ${
              compacto ? "aspect-[16/9]" : "aspect-square sm:aspect-[4/3]"
            }`}
          >
            <span
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{ background: "radial-gradient(circle at 50% 120%, #52c234 0%, transparent 60%)" }}
              aria-hidden="true"
            />
            {Icono ? (
              <Icono
                className={`h-10 w-10 text-certero transition-transform duration-500 sm:h-14 sm:w-14 ${
                  abierto ? "scale-110" : "group-hover:scale-105"
                }`}
                strokeWidth={1.2}
              />
            ) : (
              <img
                src="/logo-color.svg"
                alt=""
                aria-hidden="true"
                loading="lazy"
                className={`h-10 w-auto opacity-90 transition-transform duration-500 sm:h-14 ${
                  abierto ? "scale-110" : "group-hover:scale-105"
                }`}
              />
            )}
          </span>
        )}
        <span
          className={`absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3 sm:items-center sm:p-5 ${
            foto ? "" : "bg-gradient-to-t from-abyss/85 to-transparent sm:bg-none"
          }`}
        >
          <span className="font-display text-sm font-bold uppercase leading-tight tracking-wide text-mist sm:text-base lg:text-lg">
            {titulo}
          </span>
          <motion.span
            animate={{ rotate: abierto ? 45 : 0 }}
            transition={{ duration: reduce ? 0 : 0.4, ease: EASE }}
            data-testid={`${testid}-mas`}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-certero/60 bg-abyss/80 text-certero backdrop-blur-sm sm:h-9 sm:w-9"
            aria-hidden="true"
          >
            <Plus className="h-4 w-4" />
          </motion.span>
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: abierto ? "auto" : 0, opacity: abierto ? 1 : 0 }}
        transition={{ duration: reduce ? 0 : 0.4, ease: EASE }}
        className="overflow-hidden"
        data-testid={`${testid}-contenido`}
      >
        <div className="p-5 pt-2 sm:p-6 sm:pt-2">{children}</div>
      </motion.div>
    </div>
  );
}
